import React, {FC} from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import {BrowserRouter, Route, Routes, useNavigate} from "react-router-dom";
import * as fas from "@fortawesome/free-solid-svg-icons";
import * as far from "@fortawesome/free-regular-svg-icons";
import Keycloak from "keycloak-js";
import {NavigationDefinition} from "@jane/lib/src/components/layout/AuthenticatedLayout";
import {AuthMiddleware, AuthProvider} from "@jane/lib/src/auth/AuthProvider";
import {config} from "./config";
import { ToastContextProvider } from '@jane/lib/src/toasts/toasts';
import OverviewPage from './pages/OverviewPage';
import {EmployeeContextProvider} from "./resources/EmployeeContext";
import {TaskContextProvider} from "./resources/TaskContext";
import {ClientContextProvider} from "./resources/ClientContext";
import {ClientContactContextProvider} from "./resources/ClientContactContext";
import {TeamContextProvider} from "./resources/TeamContext";
import { AuthContextProvider } from '@jane/lib/src/auth/context';
import TasksListPage from "./pages/tasks/TasksListPage";
import EmployeesListPage from "./pages/employees/EmployeesListPage";
import EmployeesDetailPage from "./pages/employees/EmployeesDetailPage";
import ClientsListPage from "./pages/clients/ClientsListPage";
import ClientsDetailsPage from './pages/clients/ClientsDetailsPage';
import SettingsPage from "./pages/settings/SettingsPage";
import SettingsSocialCardPage from "./pages/settings/SettingsSocialCardPage";
import ChatPage from "./pages/chat/ChatPage";
import BroadcastsListPage from "./pages/broadcasts/BroadcastsListPage";
import BroadcastsCreatePage from "./pages/broadcasts/BroadcastCreatePage";
import BroadcastsDetailsPage from "./pages/broadcasts/BroadcastDetailsPage";
import {LearnDashboardPage} from "./pages/learn/LearnDashboardPage";
import { RefreshController } from '@jane/lib/src/components/RefreshController';
import {LearnModuleDetailPage} from "./pages/learn/LearnModuleDetailsPage";
import {LearnComponentDetailPage} from "./pages/learn/LearnComponentDetailsPage";
import {TelephonePage} from "./pages/telephone/TelephonePage";
import {TaskDetailsPage} from "./pages/tasks/TasksDetailsPage";
import * as Sentry from "@sentry/react";
import {Toolbar} from "./components/Toolbar";
import {TelephoneAuthenticationProvider} from "./telephone/context/TelephoneAuthenticationContext";
import {TelephoneSocketProvider} from "./telephone/context/TelephoneSocketContext";
import {TelephoneMatchesProvider} from "./telephone/context/TelephoneMatchesContext";
import Logs from "./pages/settings/Logs";
import TelephoneSettingsPage from "./pages/settings/TelephoneSettingsPage";
import {FlowManagerPage} from "./pages/flows/FlowManagerPage";
import {FlowDetailsPage} from "./pages/flows/FlowDetailsPage";
import {LearnCategoryDetailsPage} from "./pages/learn/LearnCategoryDetailsPage";
import {ChatTokenContextProvider} from "../../lib/src/chat/context/chat-token-context";
import {ChatProviderAdapter} from "./components/chat-provider-adapter";
import {MatchesPage} from "./pages/telephone/MatchesPage";
import CallbackTemplatesSettingsPage from "./pages/settings/CallbackTemplatesSettingsPage";
import {LearnDashboardExplorerPage} from "./pages/learn/LearnDashboardExplorerPage";
import {TussenOnsOverviewPage} from "./pages/tussen-ons/TussenOnsOverviewPage";
import {TussenOnsBroadcastListPage} from "./pages/tussen-ons/TussenOnsBroadcastListPage";
import { TussenOnsBroadcastDetailPage } from "./pages/tussen-ons/TussenOnsBroadcastDetailPage";
import {ProfileContextProvider} from "./resources/ProfileContext";
import {ProfilePage} from "./pages/profile/ProfilePage";
import {DocumentationLibraryPage} from "./pages/documentationLibrary/DocumentationLibraryPage";
import {ModalProvider} from "../../lib/src/components/layout/ModalProvider";
import {NotificationLogs} from "./pages/settings/NotificationLogs";
import {LearnDashboardsProgramPage} from "./pages/learn/LearnDashboardsProgramPage";
import {LearnDashboardTeamPage} from "./pages/learn/LearnDashboardTeamPage";
import {
  DocumentationLibrarySpaceSettingsPage
} from "./pages/documentationLibrary/DocumentationLibrarySpaceSettingsPage";
import {generateDynamicColorSchemeCSS} from "@ventumsoftware/react-toolkit";
import colors from "tailwindcss/colors";


if (config.sentryDsn) {
  Sentry.init({
    dsn: config.sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/api\.miep\.janehelpt\.eu\/v1/,
      /^wss:\/\/api\.miep\.janehelpt\.eu\/v1/,
      /^https:\/\/chat01\.janehelpt\.eu\/v1/,
      /^https:\/\/api\.miep\.online\/v1/,
      /^wss:\/\/api\.miep\.online\/v1/,
      /^https:\/\/chat\.miep\.online\/v1/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const authClient = new Keycloak({
  url: config.authRealmUrl,
  realm: config.authRealm,
  clientId: config.authClientId,
})

const navigation: NavigationDefinition = [
  {
    link: '/',
    text: 'Overzicht',
    icon: fas.faHome,
  },
  {
    link: '/telephone',
    text: 'Telefonie',
    icon: fas.faPhone,
    requiredPermissions: ['canAccessPhoneReception'],
  },
  {
    link: '/tasks',
    text: 'Taken',
    icon: far.faCheckSquare,
    // TODO: Permissions
  },
  {
    link: '/clients',
    text: 'Cliënten',
    icon: fas.faPerson,
    requiredPermissions: ['canAccessClients'],
  },
  {
    link: '/employees',
    text: 'Medewerkers',
    icon: fas.faPeopleGroup,
    requiredPermissions: ['canAccessEmployees'],
  },
  {
    link: '/broadcasts',
    text: 'Publicaties',
    icon: fas.faBullhorn,
    requiredPermissions: ['canManageBroadcasts'],
  },
  {
    link: '/tussen-ons',
    text: 'Tussen Ons',
    icon: fas.faCircleUser,
    requiredPermissions: ['canAccessBroadcasts'],
  },
  {
    link: '/learn',
    text: 'Kennisbank',
    icon: fas.faGraduationCap,
    requiredPermissions: ['canManageLearn'],
  },
  {
    link: '/dashboards-learn',
    text: 'Dashboards',
    icon: fas.faGraduationCap,
    requiredPermissions: ['canManageLearn'],
  },
  // {
  //   link: '/samen-werken',
  //   text: 'Samen Werken',
  //   icon: fas.faFile,
  //   requiredPermissions: ['canAccessDocumentation'],
  // },
  {
    link: '/flows',
    text: 'Flow Builder',
    icon: fas.faCodeBranch,
    requiredPermissions: ['canManageFlows'],
  },
  {
    link: '/chat',
    text: 'Chat',
    icon: far.faComments,
    // TODO: Permissions
  },
  {
    link: '/settings',
    text: 'Instellingen',
    icon: fas.faCog,
    requiredPermissions: ['canManageSettings'],
  },
]
const App: FC = () => {
  const navigate = useNavigate()
  return <AuthMiddleware navigation={navigation} navigate={navigate} config={config} allowedRoles={["employee", "telephone", "coordinator", "manager"]} appTitle={""} appSubTitle={"voor Medewerkers"} image={'/img/title_miep.png'}>
    <RefreshController>
      <ModalProvider>
        <ProfileContextProvider>
          <EmployeeContextProvider>
            <ClientContextProvider>
              <ClientContactContextProvider>
                <TaskContextProvider>
                  <TeamContextProvider>
                    <ChatProviderAdapter config={config}>
                      <TelephoneAuthenticationProvider>
                        <TelephoneSocketProvider>
                          <TelephoneMatchesProvider>
                            <main className={"flex-1 overflow-y-scroll"} style={{height: 'calc(100vh - 5rem)'}}>
                              <div className={"mx-6 my-8"}>
                                <Routes>
                                  <Route path={"/"} element={<OverviewPage/>}/>
                                  <Route path={"/tasks"} element={<TasksListPage/>}/>
                                  <Route path={"/tasks/:id"} element={<TaskDetailsPage/>}/>
                                  <Route path={"/employees"} element={<EmployeesListPage/>}/>
                                  <Route path={"/employees/:id"} element={<EmployeesDetailPage/>}/>
                                  <Route path={"/clients"} element={<ClientsListPage/>}/>
                                  <Route path={"/clients/:id"} element={<ClientsDetailsPage/>}/>
                                  <Route path={"/settings"} element={<SettingsPage/>}/>
                                  <Route path={"/settings/social_card"} element={<SettingsSocialCardPage/>}/>
                                  <Route path={"/settings/telephone"} element={<TelephoneSettingsPage />} />
                                  <Route path={"/settings/callback_templates"} element={<CallbackTemplatesSettingsPage />} />
                                  <Route path={"/logs"} element={<Logs/>}/>
                                  <Route path={"/settings/notification_logs"} element={<NotificationLogs/>}/>
                                  <Route path={"/chat"} element={<ChatPage/>}/>
                                  <Route path={"/broadcasts"} element={<BroadcastsListPage/>}/>
                                  <Route path={"/broadcasts/new"} element={<BroadcastsCreatePage/>}/>
                                  <Route path={"/broadcasts/:broadcastId"} element={<BroadcastsDetailsPage/>}/>
                                  <Route path={"/telephone"} element={<TelephonePage/>}/>
                                  <Route path={"/telephone/matches"} element={<MatchesPage/>}/>
                                  <Route path={"/learn"} element={<LearnDashboardPage/>}/>
                                  <Route path={"/dashboards-learn"} element={<LearnDashboardExplorerPage/>}/>
                                  <Route path={"/dashboards-learn/programs"} element={<LearnDashboardsProgramPage/>}/>
                                  <Route path={"/dashboards-learn/team"} element={<LearnDashboardTeamPage/>}/>
                                  <Route path={"/learn/categories/:categoryId"} element={<LearnCategoryDetailsPage/>}/>
                                  <Route path={"/learn/modules/:moduleId"} element={<LearnModuleDetailPage/>}/>
                                  <Route path={"/learn/modules/:moduleId/:componentId"} element={<LearnComponentDetailPage/>}/>
                                  <Route path={"/flows"} element={<FlowManagerPage />}/>
                                  <Route path={"/flows/:flowId"} element={<FlowDetailsPage />}/>
                                  <Route path={"/tussen-ons"} element={<TussenOnsOverviewPage />}/>
                                  <Route path={"/tussen-ons/nieuws"} element={<TussenOnsBroadcastListPage />}/>
                                  <Route path={"/tussen-ons/nieuws/:broadcastId"} element={<TussenOnsBroadcastDetailPage />}/>
                                  <Route path={"/samen-werken"} element={<DocumentationLibraryPage />} />
                                  <Route path={"/samen-werken/space/:spaceId"} element={<DocumentationLibraryPage />} />
                                  <Route path={"/samen-werken/space/:spaceId/page/:pageId"} element={<DocumentationLibraryPage />} />
                                  <Route path={"/samen-werken/:spaceId/instellingen"} element={<DocumentationLibrarySpaceSettingsPage />} />
                                  <Route path={"/profile"} element={<ProfilePage />}/>
                                </Routes>
                              </div>
                            </main>
                            <Toolbar />
                          </TelephoneMatchesProvider>
                        </TelephoneSocketProvider>
                      </TelephoneAuthenticationProvider>
                    </ChatProviderAdapter>
                  </TeamContextProvider>
                </TaskContextProvider>
              </ClientContactContextProvider>
            </ClientContextProvider>
          </EmployeeContextProvider>
        </ProfileContextProvider>
      </ModalProvider>
    </RefreshController>
  </AuthMiddleware>
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(<>
  <style>{generateDynamicColorSchemeCSS(colors.red)}</style>
    <BrowserRouter>
      <AuthProvider authClient={authClient} isGuestRoute={() => false}>
        {/*<React.StrictMode>*/}
        <AuthContextProvider config={config}>
          <ChatTokenContextProvider>
            <ToastContextProvider>
              <App />
            </ToastContextProvider>
          </ChatTokenContextProvider>
        </AuthContextProvider>
        {/*</React.StrictMode>*/}
      </AuthProvider>
    </BrowserRouter>
</>)

